<template>
  <div class="card location-input-form">
    <div class="card-content">
      <location-form v-model="location"  />

      <hr/>

      <div class="columns">

        <!-- location contacts selection -->
        <div class="column is-half">
          <label class="label">Location Contacts</label>
          <contact-form :company-location="location" contact-type="contact" v-model="location.contact1" />
          <contact-form :company-location="location" contact-type="contact" v-model="location.contact2" />
        </div>

        <!-- date selection -->
        <!-- time window selection -->
        <div class="column">
          <label class="label">Date</label>
          <date-picker v-model="date" :config="datePickerOptions"/>
          <label>
            <input type="checkbox" v-model="isFlexibleDate" />
            Dates are flexible
            <b-tooltip label="Give the driver a date-range in place of just a single day." position="is-right" multilined>
              <font-awesome-icon icon="info-circle" />
            </b-tooltip>
          </label>
        </div>


        <div class="column">
          <b-field label="Dock/Gate" class="dock-gate-label">
            <b-input v-model="location.companyJobPointDock"
                     name="street address"></b-input>
          </b-field>

          <div class="arrival-window-wrapper">
            <label class="label">Arrival Window</label>
            <b-field label="From">
              <div class="datepicker-wrapper">
                <date-picker v-model="minTime" ref="fromTime" :config="timePickerOptions" />
              </div>
            </b-field>

            <b-field label="To">
              <div class="datepicker-wrapper">
                <date-picker v-model="maxTime" ref="toTime" :config="toTimePickerOptions" />
              </div>
            </b-field>
          </div>
        </div>


      </div>
    </div>
    <div class="card-footer">
      <div class="card-footer-item">
        <button type="button"
                class="button is-primary save-location-btn"
                :class="{ 'is-loading': isCreatingLocation }"
                :disabled="isCreatingLocation || !canSaveLocation"
                @click="addLocation">
          Save Location
        </button>
        <b-tooltip label="When you add a new location, we will automatically save it so you do not have to type it again in the future." position="is-right" multilined>
          <font-awesome-icon icon="info-circle" />
        </b-tooltip>
      </div>
      <div class="card-footer-item">
        <button type="button" class="button" @click="cancelLocationChange">Cancel</button>
      </div>
    </div>

  </div>
</template>

<script>
import LocationForm from '@/components/FormLocation'
import LocationSearch from '@/components/RolodexLocationSearch'
import ContactForm from '@/components/FormContact'
import DatePicker from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { DateTime } from 'luxon'
import { format as formatDate, addHours } from 'date-fns'
import hasEmptyValues from '@/helpers/hasEmptyValues'
import { isEmpty } from 'lodash-es'

import RolodexApi from '@/api-interface/rolordex'

const timestampFormattingToken = 'YYYY-MM-DDTHH:mm:ssZ'

function transformContactToRolodex(contact) {
  const payload = {
    companyRolodexContactEmailAddress: contact.email,
    companyRolodexContactFullName: contact.name,
    companyRolodexContactPhones: [],
    companyRolodexContactKey: contact.companyRolodexContactKey
  }

  if (contact.phone) {
    payload.companyRolodexContactPhones = [
      {
        companyRolodexContactPhoneDigits: contact.phone,
        companyRolodexContactPhonePrimaryFlag: true,
        companyRolodexContactPhoneType: 'n/a'
      }
    ]
  }

  return payload
}

const defaultLocation = () => ({
  companyRolodexJobLocationAddresses: {
    companyRolodexJobLocationAddressZip: null,
    companyRolodexJobLocationAddressState: null,
    companyRolodexJobLocationAddressCity: null,
    companyRolodexJobLocationAddressStreet1: null,
    companyRolodexJobLocationAddressStreet2: null,
    companyRolodexJobLocationGpsLatitudeCoordinate: null,
    companyRolodexJobLocationGpsLongitudeCoordinate: null
  },

  companyRolodexJobLocationName: null,
  CompanyRolodexContactKey1: null,
  CompanyRolodexContactKey2: null,
  CompanyRolodexJobLocationAddressKey: null,
  companyJobPointComments: null,
  companyJobPointDateEarliest: null,
  companyJobPointDateLatest: null,
  companyJobPointDock: null,
  companyJobPointType: null,

  contact1: { companyRolodexContactKey: null, name: '', phone: null, email: '' },
  contact2: { companyRolodexContactKey: null, name: '', phone: null, email: '' }
})

/**
 * Takes the earliest and latest dates from a jobPoint and breaks them out in to individual parts that can be restored
 * and interpreted by this component
 *
 * @param {String} earliestDate
 * @param {String} latestDate
 * @returns {{isFlexibleDate: boolean, date: string, minTime: string, maxTime: string}}
 */
function getDateFromProp({ earliestDate, latestDate }) {
  const date1 = formatDate(earliestDate, 'YYYY-MM-DD')
  const date2 = formatDate(latestDate, 'YYYY-MM-DD')

  let isFlexibleDate = false
  let date = date1

  // if dates are not equal..... then it is a date range
  if (date1 !== date2) {
    isFlexibleDate = true
    date = `${date1} to ${date2}`
  }
  return {
    isFlexibleDate,
    date,
    minTime: formatDate(earliestDate, 'HH:mm'),
    maxTime: formatDate(latestDate, 'HH:mm')
  }
}

export default {
  name: 'JobPoint-Create',

  props: {
    minDate: {
      type: [String, Date],
      default: () => {
        return formatDate(new Date(), 'YYYY-MM-DD')
      }
    },
    value: {
      type: Object,
      default: defaultLocation
    }
  },

  created() {
    this.copyValuePropToData()
  },

  mounted() {
    // TODO: remove this hack once the bug in the date picker is fixed: https://github.com/flatpickr/flatpickr/issues/1519
    // this.$refs.toTime.fp.open()
    // this.$refs.fromTime.fp.open()
  },

  computed: {
    /**
     * A combination of the earliest date + earliest time
     * @returns {String} Formatted date string
     */
    earliestDate() {
      const [minDate, maxDate] = this.date.split(' to ')
      const time = this.minTime
      const date = minDate || maxDate
      const timeStamp = `${date} ${time}`

      return formatDate(timeStamp, timestampFormattingToken)
    },

    /**
     * A combination of the latest date + latest time
     * @returns {String} Formatted date string
     */
    latestDate() {
      const [minDate, maxDate] = this.date.split(' to ')
      const time = this.maxTime
      const date = maxDate || minDate
      const timeStamp = `${date} ${time}`

      return formatDate(timeStamp, timestampFormattingToken)
    },

    /**
     * Config object for the date picker
     * @returns {Object}
     */
    datePickerOptions() {
      return {
        mode: this.isFlexibleDate ? 'range' : 'single',
        minDate: this.minDate,
        inline: true
      }
    },

    /**
     * Config object specific to the "To" time picker
     * @returns {Object}
     */
    toTimePickerOptions() {
      return {
        ...this.timePickerOptions,
        minDate: this.minTime
      }
    },

    /**
     * Location/Facility information but in a structure that the Rolodex API will accept
     * @returns {Object}
     */
    formattedRolodexPayload() {
      return {
        companyRolodexJobLocationName: this.location.companyRolodexJobLocationName,
        companyRolodexJobLocationAddresses: [
          {
            ...this.location.companyRolodexJobLocationAddresses,
            companyRolodexJobLocationAddressType: 'business',
            companyRolodexJobLocationAddressPrimaryFlag: true
          }
        ]
      }
    },

    /**
     * Location/Facility information but in a structure of a "JobPoint" that the JobPoint API will accept
     * @returns {Object}
     */
    formattedJobPoint() {
      return {
        ...this.location,
        timezone: '',
        CompanyRolodexContactKey1: this.location.CompanyRolodexContactKey1,
        CompanyRolodexContactKey2: this.location.CompanyRolodexContactKey2,
        // TODO: not sure this is correct
        CompanyRolodexJobLocationAddressKey: this.location.CompanyRolodexJobLocationAddressKey,
        companyJobPointComments: null,
        companyJobPointDateEarliest: this.earliestDate,
        companyJobPointDateLatest: this.latestDate,
        companyJobPointDock: this.location.companyJobPointDock
      }
    },

    /**
     * Flag indicating of all required fields have a value
     *
     * @returns {Boolean}
     */
    canSaveLocation() {
      return (
        !isEmpty(this.location.companyRolodexJobLocationName) &&
        !isEmpty(this.location.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressCity) &&
        !isEmpty(this.location.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressState) &&
        !isEmpty(this.location.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressStreet1) &&
        !isEmpty(this.location.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressZip) &&
        !isEmpty(this.date)
      )
    }
  },

  components: {
    DatePicker,
    ContactForm,
    LocationForm,
    LocationSearch
  },

  data() {
    return {
      oldJobPointData: {},
      isDirty: false,
      date: this.minDate,
      minTime: '12:00',
      maxTime: '14:00',
      isFlexibleDate: false,
      isCreatingLocation: false,
      location: defaultLocation(),
      timePickerOptions: {
        noCalendar: true,
        enableTime: true,
        /*
        onOpen() {
          // TODO: remove this hack once the bug in the date picker is fixed: https://github.com/flatpickr/flatpickr/issues/1519
          this.calendarContainer.classList.add('inline')
          window.setTimeout(() => {
            this.calendarContainer.classList.remove('arrowTop')
          }, 0)
        },
        */
        static: true,
        inline: true
      }
    }
  },

  methods: {
    copyValuePropToData() {
      this.oldJobPointData = this.value
      this.location = Object.assign({}, this.value)

      // break apart the date
      if (this.value.companyJobPointDateEarliest) {
        const dateValues = getDateFromProp({
          earliestDate: this.value.companyJobPointDateEarliest,
          latestDate: this.value.companyJobPointDateLatest
        })
        this.date = dateValues.date
        // must assign minTIme before maxTime, otherwise the watcher will clobber the "maxTime"
        this.minTime = dateValues.minTime
        this.maxTime = dateValues.maxTime
        this.isFlexibleDate = dateValues.isFlexibleDate
      }

      // format contacts in to a structure `form-contact` component can easily consume
      if (this.location.CompanyRolodexContactKey1) {
        const contact = this.location.CompanyRolodexContactKey1
        this.location.contact1 = {
          email: contact.companyRolodexContactEmailAddress,
          phone: contact.companyRolodexContactPhones[0]
            ? contact.companyRolodexContactPhones[0].companyRolodexContactPhoneDigits
            : null,
          name: contact.companyRolodexContactFullName,
          companyRolodexContactKey: contact.companyRolodexContactKey
        }
      }

      if (this.location.CompanyRolodexContactKey2) {
        const contact = this.location.CompanyRolodexContactKey2
        this.location.contact2 = {
          email: contact.companyRolodexContactEmailAddress,
          phone: contact.companyRolodexContactPhones[0]
            ? contact.companyRolodexContactPhones[0].companyRolodexContactPhoneDigits
            : null,
          name: contact.companyRolodexContactFullName,
          companyRolodexContactKey: contact.companyRolodexContactKey
        }
      }
    },

    cancelLocationChange() {
      this.$emit('input', null)
    },

    async addLocation() {
      this.oldJobPointData = {}

      if (!this.location.CompanyRolodexJobLocationAddressKey) {
        try {
          this.isCreatingLocation = true
          // no address key means that this is a new address that needs to be saved to the rolodex
          const response = await RolodexApi.addLocation({ locationInfo: this.formattedRolodexPayload })

          this.location.CompanyRolodexJobLocationAddressKey =
            response.companyRolodexJobLocationAddresses[0].companyRolodexJobLocationAddressKey
        } finally {
          this.isCreatingLocation = false
        }
      }

      // deal with contacts
      if (!hasEmptyValues(this.location.contact1)) {
        const payload = transformContactToRolodex(this.location.contact1)
        if (this.location.contact1.companyRolodexContactKey) {
          // contact is already in the address book, so just add it to the transaction
          this.location.CompanyRolodexContactKey1 = payload
        } else {
          // this is a new contact, so save it to the address book before adding to transaction
          this.location.CompanyRolodexContactKey1 = await RolodexApi.addContact({
            contactInfo: payload,
            addressKeys: [this.location.CompanyRolodexJobLocationAddressKey]
          })
        }
      } else {
        this.location.CompanyRolodexContactKey1 = null
      }

      if (!hasEmptyValues(this.location.contact2)) {
        const payload = transformContactToRolodex(this.location.contact2)
        if (this.location.contact2.companyRolodexContactKey) {
          // contact is already in the address book, so just add it to the transaction
          this.location.CompanyRolodexContactKey2 = payload
        } else {
          // this is a new contact, so save it to the address book before adding to transaction
          this.location.CompanyRolodexContactKey2 = await RolodexApi.addContact({
            contactInfo: payload,
            addressKeys: [this.location.CompanyRolodexJobLocationAddressKey]
          })
        }
      } else {
        this.location.CompanyRolodexContactKey2 = null
      }

      this.$emit('input', this.formattedJobPoint)
    }
  },

  watch: {
    'value.CompanyRolodexJobLocationAddressKey'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.copyValuePropToData()
      }
    },

    minDate() {
      window.setTimeout(() => {
        this.date = this.date || this.minDate
      }, 200)
    },

    minTime() {
      // when ever the "from" time changes... make sure the "to" time is always 2h ahead
      this.maxTime = addHours(DateTime.fromISO(this.minTime), 2)
    }
  }
}
</script>

<style>
.datepicker-wrapper .flatpickr-calendar {
  position: relative !important;
}

.arrival-window-wrapper .datepicker-wrapper .flatpickr-calendar {
  max-width: 250px;
  width: 100%;
}
</style>
<style scoped>
.datepicker-wrapper {
  position: relative;
}

.has-content-centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.flatpickr-input {
  display: none;
}

.arrival-window-wrapper {
  margin-top: 65px;
}

.dock-gate-label {
  width: 150px;
}

.save-location-btn {
  margin-right: 8px;
}
</style>
